/**
 * 🎬 Admin — Export vidéo de la démo
 *
 * Permet à un admin de lancer un export MP4 de la démo COACHDIA au format
 * paysage 16:9 (1920×1080) pour publication sur LinkedIn, YouTube, Facebook.
 * Les jobs sont exécutés en background (Playwright + ffmpeg) et le status
 * est polled toutes les 3 secondes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Film, Download, Loader2, RefreshCw, CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147', text: '#1F2937',
  muted: '#6B7280', border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F',
  goldLight: '#F3D060', ivoryDeep: '#FFFCEC', green: '#48825A', red: '#9B2C1F',
};

const STATUS_META = {
  pending: { label: 'En attente', color: T.muted, icon: Clock },
  running: { label: 'En cours',   color: T.goldDeep, icon: Loader2 },
  done:    { label: 'Prêt',       color: T.green, icon: CheckCircle2 },
  failed:  { label: 'Échec',      color: T.red, icon: XCircle },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

export default function AdminDemoExport() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const API = getApiUrl();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/demo/export/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setJobs(j.items || []);
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Poll toutes les 3s si un job est running
  useEffect(() => {
    const running = jobs.some((j) => j.status === 'running' || j.status === 'pending');
    if (!running) return undefined;
    const i = setInterval(fetchJobs, 3000);
    return () => clearInterval(i);
  }, [jobs, fetchJobs]);

  const startExport = async (orientation) => {
    setLaunching(true);
    try {
      const r = await fetch(`${API}/admin/demo/export/${orientation}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success(`Export ${orientation} démarré · job_id=${j.job_id}`);
      fetchJobs();
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    } finally {
      setLaunching(false);
    }
  };

  const downloadJob = async (jobId) => {
    try {
      const r = await fetch(`${API}/admin/demo/export/download/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coachdia-demo-${jobId}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Téléchargement lancé');
    } catch (e) {
      toast.error(`Erreur: ${e.message}`);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate('/training')}
          data-testid="admin-demo-export-back"
          className="flex items-center gap-2 text-sm mb-4 text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Retour modules admin
        </button>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p
              className="uppercase text-[11px] tracking-[0.24em] font-semibold"
              style={{ color: T.gold }}
            >
              Démo · Export vidéo
            </p>
            <h1
              className="text-3xl mt-1"
              style={{ color: '#FFF', fontFamily: 'Georgia, serif', fontWeight: 600 }}
            >
              Générer vidéo démo MP4
            </h1>
            <p className="text-sm mt-1 text-zinc-400">
              Rendu Playwright + ffmpeg · narration TTS + musique · ~2–3 min par export
            </p>
          </div>
          <button
            onClick={fetchJobs}
            disabled={loading}
            data-testid="admin-demo-export-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{ background: T.gold, color: T.ink }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Cartes de lancement */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div
            className="rounded-2xl p-5"
            style={{ background: T.bg, border: `1px solid ${T.border}` }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                }}
              >
                <Film className="w-5 h-5" style={{ color: T.ink }} />
              </div>
              <div>
                <h3
                  className="text-base"
                  style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}
                >
                  Paysage 16:9
                </h3>
                <p className="text-xs" style={{ color: T.muted }}>
                  1920×1080 · YouTube · LinkedIn · Facebook
                </p>
              </div>
            </div>
            <button
              onClick={() => startExport('horizontal')}
              disabled={launching}
              data-testid="admin-demo-export-launch-horizontal"
              className="w-full py-2.5 rounded-full text-sm font-bold disabled:opacity-50"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                color: T.ink,
              }}
            >
              {launching ? 'Démarrage…' : 'Lancer l\'export paysage'}
            </button>
          </div>

          <div
            className="rounded-2xl p-5 opacity-70"
            style={{ background: T.bg, border: `1px solid ${T.border}` }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: '#EEE7D0' }}
              >
                <Film className="w-5 h-5" style={{ color: T.muted }} />
              </div>
              <div>
                <h3
                  className="text-base"
                  style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}
                >
                  Vertical 9:16
                </h3>
                <p className="text-xs" style={{ color: T.muted }}>
                  TikTok · Reels Instagram · Shorts YouTube
                </p>
              </div>
            </div>
            <button
              onClick={() => startExport('vertical')}
              disabled={launching}
              data-testid="admin-demo-export-launch-vertical"
              className="w-full py-2.5 rounded-full text-sm font-bold disabled:opacity-50"
              style={{
                background: T.card,
                color: T.ink,
                border: `1.5px solid ${T.gold}`,
              }}
              title="Expérimental : layout mobile non optimisé pour ce format"
            >
              {launching ? 'Démarrage…' : 'Lancer l\'export vertical (beta)'}
            </button>
          </div>
        </div>

        {/* Historique jobs */}
        <h2
          className="text-sm uppercase tracking-[0.18em] mb-3"
          style={{ color: T.gold, fontWeight: 700 }}
        >
          Historique des exports
        </h2>
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: T.bg, border: `1px solid ${T.border}` }}
        >
          {loading && (
            <div className="p-10 text-center text-sm" style={{ color: T.muted }}>
              Chargement…
            </div>
          )}
          {!loading && jobs.length === 0 && (
            <div
              data-testid="admin-demo-export-empty"
              className="p-10 text-center"
              style={{ color: T.muted }}
            >
              Aucun export généré pour l'instant. Cliquez sur « Lancer l'export paysage »
              pour démarrer votre première vidéo.
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div data-testid="admin-demo-export-jobs" className="divide-y" style={{ borderColor: T.border }}>
              {jobs.map((j) => {
                const meta = STATUS_META[j.status] || STATUS_META.pending;
                const Icon = meta.icon;
                const isRunning = j.status === 'running' || j.status === 'pending';
                return (
                  <div
                    key={j.job_id}
                    className="p-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid={`admin-demo-export-row-${j.job_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Icon
                          className={`w-4 h-4 ${isRunning ? 'animate-spin' : ''}`}
                          style={{ color: meta.color }}
                        />
                        <span
                          className="text-xs font-semibold"
                          style={{ color: meta.color }}
                        >
                          {meta.label}
                        </span>
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                          style={{
                            background: T.ivoryDeep,
                            color: T.goldDeep,
                            border: `1px solid ${T.border}`,
                          }}
                        >
                          {j.orientation}
                        </span>
                        <span className="text-[11px] font-mono" style={{ color: T.muted }}>
                          {j.job_id}
                        </span>
                      </div>
                      <div className="text-[11px]" style={{ color: T.muted }}>
                        Créé {fmtDate(j.created_at)}
                        {j.finished_at && ` · Terminé ${fmtDate(j.finished_at)}`}
                        {j.output_size_bytes && ` · ${fmtSize(j.output_size_bytes)}`}
                      </div>
                      {j.error && (
                        <div className="text-xs mt-1" style={{ color: T.red }}>
                          ⚠ {j.error}
                        </div>
                      )}
                    </div>
                    {j.status === 'done' && (
                      <button
                        onClick={() => downloadJob(j.job_id)}
                        data-testid={`admin-demo-export-download-${j.job_id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold"
                        style={{
                          background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                          color: T.ink,
                        }}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Télécharger
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
